import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, LogOut, User, Shield, Package, Eye, ClipboardList, CreditCard, FileText, FileSpreadsheet, Globe, Boxes, Truck, Mail, Menu, X, IndianRupee, PackageCheck, BarChart3, BookOpen, BarChart2, Database, DollarSign, LayoutGrid, Activity, Tag, ScanLine, Sparkles, Share2, Sun, Moon, MessageCircle } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useCustomerView } from '@/hooks/useCustomerView';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import AdminOrdersButton from '@/components/AdminOrdersButton';
import CustomerPriceRequestsButton from '@/components/CustomerPriceRequestsButton';
import AdminPriceRequestsButton from '@/components/AdminPriceRequestsButton';
import ExcelImportQuotation from '@/components/ExcelImportQuotation';
import StaffPanelPickerDialog from '@/components/StaffPanelPickerDialog';
import AppToolsMenu from '@/components/landing/AppToolsMenu';
import axios from 'axios';
import { secureStorage } from '../utils/secureStorage';

const NavTooltip = ({ label, children }) => (
  <Tooltip delayDuration={100}>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent side="bottom" className="bg-gray-900 text-white text-xs font-medium px-2.5 py-1 rounded-md">
      {label}
    </TooltipContent>
  </Tooltip>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Doc Sharing admins
const DOC_SHARING_ADMINS = ['9811117751', '9999313175'];
const PRICE_MASTER_ADMINS = ['9811117751', '9999313175'];
const CHEQUE_MASTER_ADMINS = ['9999313175', '9811117751'];
const CHEQUE_ADMINS = ['9999313175', '9811117751', '9643558419'];
const ANALYTICS_ADMINS = ['9999313175', '9811117751'];
// 2026-06-17: Roshan Kumar (sub-admin) joins Bhavik + Master for the
// scan-ops cockpit and the read-only stock trail dashboard. Mirrors the
// backend allow-list in warehouse_2.py::WH2_ADMINS and
// stock_trail.py::ALLOWED_TRAIL_ADMINS — keep in sync.
const WAREHOUSE_2_ADMINS = ['9999313175', '9811117751', '9643558419'];
const STOCK_TRAIL_ADMINS = ['9999313175', '9811117751', '9643558419'];
// 2026-06-17: Coupon & MOQ Master gate now includes Roshan. Mirrors
// `coupon_master.py::COUPON_MASTER_ADMINS` — keep in sync.
const COUPON_MASTER_ADMINS = ['9999313175', '9811117751', '9643558419'];

const Navbar = () => {
  const { getTotalItems } = useCart();
  const { user, isAuthenticated, logout, token, isImpersonating } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const totalItems = getTotalItems();
  const [_isAdminRaw, setIsAdmin] = useState(false);
  const [_isStaffUserRaw, setIsStaffUser] = useState(false);
  const { isCustomerView } = useCustomerView();
  // While "View as Customer" is on, mask admin/staff identity so every
  // existing `isAdmin` / `isStaffUser` gate hides admin chrome automatically.
  // The token & permissions remain valid — exit reverses instantly.
  const isAdmin = _isAdminRaw && !isCustomerView;
  const isStaffUser = _isStaffUserRaw && !isCustomerView;
  const [isNepalDistributor, setIsNepalDistributor] = useState(false);
  const [isDomesticDistributor, setIsDomesticDistributor] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Vendor auth state
  const [vendorToken, setVendorToken] = useState(() => secureStorage.getVendorToken());
  const [vendorInfo, setVendorInfo] = useState(null);
  
  // Check if user is view_only customer
  const isViewOnly = user?.account_type === 'view_only';
  const isWarehouseStaff = user?.is_warehouse_head || user?.is_warehouse_manager
    || ['warehouse_manager', 'warehouse_head'].includes(user?.role);
  const isDocSharingAdmin = DOC_SHARING_ADMINS.includes(String(user?.mobile));
  const isChequeAdmin = CHEQUE_ADMINS.includes(String(user?.mobile));
  const isMasterChequeAdmin = CHEQUE_MASTER_ADMINS.includes(String(user?.mobile));
  const isSalesman = user?.role === 'salesman';
  
  // Excel Import Modal state
  const [showExcelImport, setShowExcelImport] = useState(false);
  const [showStaffPanelPicker, setShowStaffPanelPicker] = useState(false);
  // Direct-mint Staff Panel entry for non-admin warehouse staff
  // (office_staff, warehouse_manager, warehouse_head). Skips the
  // picker dialog per product directive — they can only open as
  // themselves anyway. Admins keep the full picker.
  const canOpenStaffPanel = isStaffUser || isWarehouseStaff;
  const handleStaffPanelClick = async () => {
    if (isAdmin) {
      setShowStaffPanelPicker(true);
      return;
    }
    try {
      const r = await axios.post(
        `${API}/staff/admin-bypass-login`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { token: staffToken, staff } = r.data || {};
      if (!staffToken || !staff) throw new Error('bad response');
      sessionStorage.setItem('staff_token', staffToken);
      sessionStorage.setItem('staff_user', JSON.stringify(staff));
      window.location.href = '/staff';
    } catch (e) {
      // Fallback: open the picker so user isn't stuck.
      setShowStaffPanelPicker(true);
    }
  };

  // ── Customer Convenience Panel onboarding callout ──
  // Show a one-time speech bubble next to the nav icon for 30 days from
  // the user's first sighting. Persists in localStorage so it survives
  // tab refreshes. Auto-hides after dismissal or 30 days, whichever first.
  const conveniencePanelIntroKey = `convenience_panel_intro_v1_${user?.customer_id || user?.mobile || 'guest'}`;
  const [showConveniencePanelIntro, setShowConveniencePanelIntro] = useState(false);
  useEffect(() => {
    if (!user || isViewOnly) return;
    try {
      const raw = localStorage.getItem(conveniencePanelIntroKey);
      const data = raw ? JSON.parse(raw) : null;
      if (data?.dismissed) return;
      const firstSeen = data?.first_seen_at || Date.now();
      if (!data) {
        localStorage.setItem(conveniencePanelIntroKey, JSON.stringify({ first_seen_at: firstSeen, dismissed: false }));
      }
      const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
      const ageMs = Date.now() - firstSeen;
      setShowConveniencePanelIntro(ageMs < THIRTY_DAYS_MS);
    } catch (_e) { /* localStorage unavailable — show nothing */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.customer_id, user?.mobile, isViewOnly]);
  const dismissConveniencePanelIntro = () => {
    try {
      const raw = localStorage.getItem(conveniencePanelIntroKey);
      const data = raw ? JSON.parse(raw) : { first_seen_at: Date.now() };
      localStorage.setItem(conveniencePanelIntroKey, JSON.stringify({ ...data, dismissed: true }));
    } catch (_e) { /* ignore */ }
    setShowConveniencePanelIntro(false);
  };

  // 2026-06-17 — the intro popover is z-50 and sits on top of the right-side
  // action area of every page (320×~180px). On the Coupon Stock Panel that
  // covered the Reprint / Print-this-row icons → user clicks fell through to
  // the popover and looked dead. Auto-dismiss on (a) any outside click and
  // (b) after 8s on screen, so the intro never blocks real work.
  useEffect(() => {
    if (!showConveniencePanelIntro) return;
    const timer = setTimeout(() => setShowConveniencePanelIntro(false), 8000);
    const onDocClick = (e) => {
      const popover = document.querySelector('[data-testid="convenience-panel-intro"]');
      if (popover && !popover.contains(e.target)) {
        setShowConveniencePanelIntro(false);
      }
    };
    // Capture phase so we run BEFORE the click reaches the target — this lets
    // the click still register on the underlying button after dismissal.
    document.addEventListener('mousedown', onDocClick, true);
    document.addEventListener('touchstart', onDocClick, true);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', onDocClick, true);
      document.removeEventListener('touchstart', onDocClick, true);
    };
  }, [showConveniencePanelIntro]);
  
  // Pending doc count for mobile navbar badge
  const [pendingDocCount, setPendingDocCount] = useState(0);
  
  // Customer cheque visibility
  const [customerHasCheques, setCustomerHasCheques] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Fetch pending doc count for admin badge
  useEffect(() => {
    if (!isDocSharingAdmin || !token) return;
    const fetchPendingCount = async () => {
      try {
        const res = await axios.get(`${API}/doc-sharing/pending-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPendingDocCount(res.data.pending_count || 0);
      } catch { setPendingDocCount(0); }
    };
    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 60000); // refresh every 60s
    return () => clearInterval(interval);
  }, [isDocSharingAdmin, token]);

  // Check if customer has cheques
  useEffect(() => {
    if (!isAuthenticated || !token || isAdmin || isStaffUser) return;
    const checkCheques = async () => {
      try {
        const res = await axios.get(`${API}/cheques/my-cheques`, { headers: { Authorization: `Bearer ${token}` } });
        setCustomerHasCheques(res.data.has_cheques || false);
      } catch { setCustomerHasCheques(false); }
    };
    checkCheques();
  }, [isAuthenticated, token, isAdmin, isStaffUser]);

  // Fetch vendor info if vendor_token exists - re-check on every route change
  useEffect(() => {
    const vt = secureStorage.getVendorToken();
    setVendorToken(vt);
    if (vt && !isAuthenticated) {
      fetch(`${API}/nto/vendor-auth/me`, { headers: { Authorization: `Bearer ${vt}` } })
        .then(r => { if (!r.ok) throw new Error(); return r.json(); })
        .then(d => setVendorInfo(d.vendor))
        .catch(() => { localStorage.removeItem('vendor_token'); setVendorToken(null); setVendorInfo(null); });
    } else if (!vt) {
      setVendorInfo(null);
    }
  }, [isAuthenticated, location.pathname]);

  useEffect(() => {
    const checkAdmin = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/admin/check`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsAdmin(response.data.is_admin);
        } catch {
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    };
    
    const checkDistributorAccess = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/export-orders/distributor/check-access`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsNepalDistributor(response.data.has_access);
        } catch {
          setIsNepalDistributor(false);
        }
      }
    };

    const checkDomesticDistributorAccess = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/distributor-panel/check-access`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          // For non-admins only — admins already have a "Panels" dropdown.
          setIsDomesticDistributor(!!response.data.has_access && !response.data.is_admin);
        } catch {
          setIsDomesticDistributor(false);
        }
      } else {
        setIsDomesticDistributor(false);
      }
    };

    checkAdmin();
    checkDistributorAccess();
    checkDomesticDistributorAccess();
    setIsStaffUser(user?.is_staff_user || false);
  }, [token, user]);

  // Hide Navbar entirely on staff portal routes - MUST be after ALL hooks
  if (location.pathname.startsWith('/staff')) return null;
  // The /auth page has its own header (components/landing/AuthLanding.jsx)
  if (location.pathname === '/auth') return null;

  // Salesman: restricted navbar — they ONLY see the Salesman Panel button +
  // profile/logout. All admin/staff/customer nav is hidden.
  const isSalesmanOnly = isSalesman && !isAdmin;

  // Desktop action buttons (hidden on mobile)
  const DesktopActions = () => {
    // For salesmen: ONLY show Salesman Panel button — nothing else.
    if (isSalesmanOnly) {
      return (
        <Button
          onClick={() => navigate('/salesman')}
          variant="default"
          size="sm"
          className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white h-9 px-4"
          data-testid="salesman-panel-btn"
          title="Salesman Panel"
        >
          <ShoppingCart className="w-4 h-4" />
          <span className="text-sm font-medium">Salesman Panel</span>
        </Button>
      );
    }
    // Desktop, admins: fixed shortcuts + pins + Go to (Ctrl K) + Tools menu (AppToolsMenu, mounted
    // directly in the navbar below so it keeps its open state), then price requests / orders / cart.
    if (isAdmin && !isViewOnly) {
      return (
        <>
          <div className="w-px h-5 bg-gray-300 mx-1" />
          <NavTooltip label="Price Requests"><span><AdminPriceRequestsButton /></span></NavTooltip>
          <NavTooltip label="Orders"><span><AdminOrdersButton /></span></NavTooltip>
          <NavTooltip label="Cart">
            <Button onClick={() => navigate('/cart')} variant="outline" className="relative rounded-md h-8 w-8 p-0" data-testid="nav-cart-button">
              <ShoppingCart className="w-4 h-4" />
              {totalItems > 0 && (
                <Badge className="absolute -top-2 -right-2 h-4 w-4 flex items-center justify-center p-0 bg-primary text-white text-[10px] rounded-full" data-testid="cart-badge">{totalItems}</Badge>
              )}
            </Button>
          </NavTooltip>
        </>
      );
    }
    return (
    <>
      {/* God Mode Button - Desktop Only for Admins */}
      {isAdmin && !isViewOnly && (
        <NavTooltip label="God View">
        <Button
          onClick={() => navigate('/admin/god-mode')}
          variant="ghost"
          size="sm"
          className="hidden lg:flex items-center text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
          data-testid="god-mode-shortcut"
        >
          <Eye className="w-4 h-4" />
        </Button>
        </NavTooltip>
      )}

      {/* 2026-02-28 · WhatsApp Integration Panel — Bhavik (9999313175)
          and Vinit Oswal (9811117751). Both run the panel day-to-day
          (RC-WAHUB-SUBADMIN). Brand green so operators spot it
          immediately. */}
      {isAdmin && !isViewOnly && ['9999313175', '9811117751'].includes(user?.mobile) && (
        <NavTooltip label="WhatsApp Hub">
        <Button
          onClick={() => navigate('/admin/whatsapp-hub')}
          variant="ghost"
          size="sm"
          className="hidden lg:flex items-center text-[#25D366] hover:text-[#1DA851] hover:bg-emerald-50 h-8 w-8 p-0"
          data-testid="wa-hub-shortcut"
          title="WhatsApp Integration Panel"
        >
          <MessageCircle className="w-4 h-4 fill-current" />
        </Button>
        </NavTooltip>
      )}
      
      {/* Admin Panel Button */}
      {isAdmin && !isViewOnly && (
        <NavTooltip label="Admin Panel">
        <Button
          onClick={() => navigate('/admin')}
          variant="ghost"
          size="sm"
          className="hidden md:flex items-center text-blue-700 hover:text-blue-800 hover:bg-blue-50 h-8 w-8 p-0"
          data-testid="admin-panel-nav-btn"
        >
          <Shield className="w-4 h-4" />
        </Button>
        </NavTooltip>
      )}
      
      {/* Task Manager Button */}
      {/* 2026-07-21 · RC-146 — warehouse_head / warehouse_manager also
          get the Task Manager shortcut. They run the daily operations
          floor and need access to task assignment, tallies, and the
          billing tab the same way admins/staff do. */}
      {(isAdmin || isStaffUser || isWarehouseStaff) && !isViewOnly && (
        <NavTooltip label="Task Manager">
        <Button
          onClick={() => navigate('/task-manager')}
          variant="ghost"
          size="sm"
          className="hidden md:flex items-center text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 w-8 p-0"
          data-testid="task-manager-btn"
        >
          <ClipboardList className="w-4 h-4" />
        </Button>
        </NavTooltip>
      )}
      
      {/* Payments Button - For Admin Only */}
      {isAdmin && !isViewOnly && (
        <NavTooltip label="Payments">
        <Button
          onClick={() => navigate('/admin/payments')}
          variant="ghost"
          size="sm"
          className="hidden md:flex items-center text-green-600 hover:text-green-700 hover:bg-green-50 h-8 w-8 p-0"
          data-testid="payments-btn"
        >
          <CreditCard className="w-4 h-4" />
        </Button>
        </NavTooltip>
      )}
      
      {/* Distributor Panel Button - For Nepal Distributor Only */}
      {isNepalDistributor && !isAdmin && (
        <Button
          onClick={() => navigate('/distributor')}
          variant="default"
          size="sm"
          className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white h-9 px-4"
          data-testid="distributor-panel-btn"
          title="Distributor Panel"
        >
          <Globe className="w-4 h-4" />
          <span className="text-sm font-medium">Distributor Panel</span>
        </Button>
      )}

      {/* Distributor Panel Button - For Domestic (Indian) Distributor */}
      {isDomesticDistributor && !isAdmin && (
        <Button
          onClick={() => navigate('/dist')}
          variant="default"
          size="sm"
          className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white h-9 px-4"
          data-testid="domestic-distributor-panel-btn"
          title="Distributor Panel"
        >
          <LayoutGrid className="w-4 h-4" />
          <span className="text-sm font-medium">Distributor Panel</span>
        </Button>
      )}

      {/* Salesman Panel Button - For Salesman Only */}
      {isSalesman && !isAdmin && (
        <Button
          onClick={() => navigate('/salesman')}
          variant="default"
          size="sm"
          className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white h-9 px-4"
          data-testid="salesman-panel-btn"
          title="Salesman Panel"
        >
          <ShoppingCart className="w-4 h-4" />
          <span className="text-sm font-medium">Salesman Panel</span>
        </Button>
      )}

      {/* Admin: All Panels Dropdown */}
      {/* Staff Panel — standalone button for non-admin sub-admins /
          office staff (is_staff_user) who don't get the Panels
          dropdown. Admins access Staff Panel from inside the Panels
          hover menu (see below) — the standalone button was removed
          for them on 2026-07-02 per user request to declutter the
          admin navbar. */}
      {canOpenStaffPanel && !isAdmin && !isViewOnly && (
        <NavTooltip label="Staff Panel (warehouse worker view)">
          <Button
            onClick={handleStaffPanelClick}
            variant="ghost"
            size="sm"
            className="hidden md:flex items-center text-indigo-700 hover:text-indigo-800 hover:bg-indigo-50 h-8 gap-1 text-xs font-semibold"
            data-testid="navbar-staff-panel-btn"
          >
            <span>👥</span> Staff Panel
          </Button>
        </NavTooltip>
      )}

      {isAdmin && !isViewOnly && (
        <div className="relative group">
          <Button variant="ghost" size="sm" className="hidden md:flex items-center text-gray-600 hover:text-gray-800 hover:bg-gray-100 h-8 gap-1 text-xs" data-testid="all-panels-btn">
            <LayoutGrid className="w-4 h-4" /> Panels
          </Button>
          <div className="absolute right-0 top-full mt-1 w-52 bg-white border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
            {[
              { label: 'Nepal Distributor', path: '/distributor', icon: '🇳🇵' },
              { label: 'Domestic Distributor', path: '/dist', icon: '🏪' },
              { label: 'Salesman Panel', path: '/salesman', icon: '🛒' },
              { label: 'Warehouse Head', path: '/warehouse-head', icon: '🏭' },
              { label: 'Warehouse Manager Panel', path: '/warehouse', icon: '📦' },
              { label: 'Mechanic Panel', path: '/mechanic', icon: '🔧' },
            ].map(p => (
              <button key={p.path} onClick={() => navigate(p.path)} className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2 border-b last:border-b-0" data-testid={`panel-link-${p.path.slice(1)}`}>
                <span>{p.icon}</span> {p.label}
              </button>
            ))}
            {/* Staff Panel — opens picker dialog (test-as-self OR impersonate-staff). */}
            <button
              onClick={() => setShowStaffPanelPicker(true)}
              className="w-full text-left px-3 py-2 text-xs hover:bg-indigo-50 flex items-center gap-2 border-t bg-indigo-50/40 font-semibold text-indigo-800"
              data-testid="panel-link-staff"
            >
              <span>👥</span> Staff Panel
            </button>
          </div>
        </div>
      )}
      
      {/* NTO Panel Button */}
      {isAdmin && !isViewOnly && DOC_SHARING_ADMINS.includes(String(user?.mobile)) && (
        <NavTooltip label="NTO Procurement">
        <Button
          onClick={() => navigate('/nto')}
          variant="ghost"
          size="sm"
          className="hidden md:flex items-center text-orange-600 hover:text-orange-700 hover:bg-orange-50 h-8 w-8 p-0"
          data-testid="nto-panel-btn"
        >
          <Boxes className="w-4 h-4" />
        </Button>
        </NavTooltip>
      )}
      
      {/* Document Sharing Panel Button - Only for specific admins */}
      {isDocSharingAdmin && isAdmin && !isViewOnly && (
        <NavTooltip label="Document Sharing">
        <Button
          onClick={() => navigate('/doc-sharing')}
          variant="ghost"
          size="sm"
          className="hidden md:flex items-center text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 h-8 w-8 p-0"
          data-testid="doc-sharing-btn"
        >
          <Mail className="w-4 h-4" />
        </Button>
        </NavTooltip>
      )}
      
      {/* Price Master Button - Only for specific admins */}
      {isAdmin && !isViewOnly && PRICE_MASTER_ADMINS.includes(String(user?.mobile)) && (
        <NavTooltip label="Price Master">
        <Button
          onClick={() => navigate('/price-master')}
          variant="ghost"
          size="sm"
          className="hidden md:flex items-center text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 h-8 w-8 p-0"
          data-testid="price-master-btn"
        >
          <IndianRupee className="w-4 h-4" />
        </Button>
        </NavTooltip>
      )}

      {/* All Products / Dead Stock Panel - Admin Only */}
      {isAdmin && !isViewOnly && (
        <NavTooltip label="All Products (Dead Stock)">
        <Button
          onClick={() => navigate('/admin/all-products')}
          variant="ghost"
          size="sm"
          className="hidden md:flex items-center text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 h-8 w-8 p-0"
          data-testid="all-products-btn"
        >
          <Package className="w-4 h-4" />
        </Button>
        </NavTooltip>
      )}

      {/* Coupon & MOQ Master Button - Master + Bhavik + Roshan (sub-admin, 2026-06-17) */}
      {isAdmin && !isViewOnly && COUPON_MASTER_ADMINS.includes(String(user?.mobile)) && (
        <NavTooltip label="Coupon & MOQ Master">
        <Button
          onClick={() => navigate('/admin/coupon-master')}
          variant="ghost"
          size="sm"
          className="hidden md:flex items-center text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 h-8 w-8 p-0"
          data-testid="coupon-master-btn"
        >
          <Tag className="w-4 h-4" />
        </Button>
        </NavTooltip>
      )}

      {/* Warehouse 2.0 — scan-driven ops */}
      {isAdmin && !isViewOnly && WAREHOUSE_2_ADMINS.includes(String(user?.mobile)) && (
        <NavTooltip label="Warehouse 2.0 (Scan Ops)">
        <Button
          onClick={() => navigate('/admin/warehouse-2')}
          variant="ghost"
          size="sm"
          className="hidden md:flex items-center text-fuchsia-600 hover:text-fuchsia-700 hover:bg-fuchsia-50 h-8 w-8 p-0"
          data-testid="warehouse-2-btn"
        >
          <ScanLine className="w-4 h-4" />
        </Button>
        </NavTooltip>
      )}
      
      {/* Warehouse Management Button - Only for admins */}
      {isAdmin && !isViewOnly && (
        <NavTooltip label="Warehouse Mgmt">
        <Button
          onClick={() => navigate('/warehouse-setup')}
          variant="ghost"
          size="sm"
          className="hidden md:flex items-center text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-8 w-8 p-0"
          data-testid="warehouse-setup-btn"
        >
          <Boxes className="w-4 h-4" />
        </Button>
        </NavTooltip>
      )}
      
      {/* Warehouse Management Button - Only for admins */}
      {isAdmin && !isViewOnly && (
        <NavTooltip label="Warehouse Management">
        <Button
          onClick={() => navigate('/warehouse-ops')}
          variant="ghost"
          size="sm"
          className="hidden md:flex items-center text-rose-600 hover:text-rose-700 hover:bg-rose-50 h-8 w-8 p-0"
          data-testid="warehouse-management-btn"
        >
          <PackageCheck className="w-4 h-4" />
        </Button>
        </NavTooltip>
      )}
      
      {/* Warehouse Intelligence Button - Only for admins */}
      {isAdmin && !isViewOnly && (
        <NavTooltip label="Warehouse Intel">
        <Button
          onClick={() => navigate('/warehouse-intel')}
          variant="ghost"
          size="sm"
          className="hidden md:flex items-center text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 h-8 w-8 p-0"
          data-testid="warehouse-intel-btn"
        >
          <BarChart3 className="w-4 h-4" />
        </Button>
        </NavTooltip>
      )}

      {/* Stock Trail Hub — Master + Bhavik + Roshan (sub-admin, 2026-06-17) */}
      {STOCK_TRAIL_ADMINS.includes(String(user?.mobile)) && !isViewOnly && !isCustomerView && (
        <NavTooltip label="Stock Trail Hub">
        <Button
          onClick={() => navigate('/warehouse/stock-trail')}
          variant="ghost"
          size="sm"
          className="hidden md:flex items-center text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 h-8 w-8 p-0"
          data-testid="stock-trail-btn"
        >
          <Activity className="w-4 h-4" />
        </Button>
        </NavTooltip>
      )}
      
      {/* Cheque Manager - visible for all staff on desktop */}
      {(isAdmin || isStaffUser) && !isViewOnly && (
        <NavTooltip label="Cheque Manager">
        <Button
          onClick={() => navigate('/cheque-manager')}
          variant="ghost"
          className="hidden md:flex items-center text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-8 w-8 p-0"
          data-testid="desktop-cheque-manager-btn"
        >
          <CreditCard className="w-4 h-4" />
        </Button>
        </NavTooltip>
      )}

      {/* Analytics Dashboard - master admins only */}
      {ANALYTICS_ADMINS.includes(String(user?.mobile)) && !isViewOnly && !isCustomerView && (
        <NavTooltip label="Analytics">
        <Button
          onClick={() => navigate('/analytics')}
          variant="ghost"
          className="hidden md:flex items-center text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 h-8 w-8 p-0"
          data-testid="desktop-analytics-btn"
        >
          <BarChart2 className="w-4 h-4" />
        </Button>
        </NavTooltip>
      )}
      
      {/* Tally Integration - master admins only */}
      {ANALYTICS_ADMINS.includes(String(user?.mobile)) && !isViewOnly && !isCustomerView && (
        <NavTooltip label="Tally Integration">
        <Button
          onClick={() => navigate('/tally-integration')}
          variant="ghost"
          className="hidden md:flex items-center text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 w-8 p-0"
          data-testid="desktop-tally-btn"
        >
          <Database className="w-4 h-4" />
        </Button>
        </NavTooltip>
      )}

      {/* API Cost Tracker - master admin only */}
      {String(user?.mobile) === '9999313175' && !isViewOnly && !isCustomerView && (
        <NavTooltip label="API Costs">
        <Button
          onClick={() => navigate('/admin/api-costs')}
          variant="ghost"
          className="hidden md:flex items-center text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 h-8 w-8 p-0"
          data-testid="desktop-api-costs-btn"
        >
          <DollarSign className="w-4 h-4" />
        </Button>
        </NavTooltip>
      )}

      {/* Admin Price Requests Button */}
      {(isAdmin || isStaffUser) && !isViewOnly && (
        <NavTooltip label="Price Requests">
          <span><AdminPriceRequestsButton /></span>
        </NavTooltip>
      )}
      
      {/* Customer Price Requests Button */}
      {!isAdmin && !isStaffUser && !isViewOnly && (
        <NavTooltip label="Price Requests">
          <span><CustomerPriceRequestsButton /></span>
        </NavTooltip>
      )}

      {/* Customer Convenience Panel (B2B → B2B2C quotations) */}
      {!isViewOnly && (
        <div className="relative">
          <NavTooltip label="Customer Convenience Panel">
          <Button
            onClick={() => { dismissConveniencePanelIntro(); navigate('/customer-quotations'); }}
            variant="outline"
            size="icon"
            className="hidden md:flex rounded-md h-8 w-8 border-amber-400 text-amber-700 hover:bg-amber-50 hover:text-amber-800 relative"
            data-testid="nav-customer-quotations-btn"
          >
            <Sparkles className="w-4 h-4" />
            {showConveniencePanelIntro && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-ping" aria-hidden="true" />
            )}
          </Button>
          </NavTooltip>
          {showConveniencePanelIntro && (
            <div
              className="hidden md:block absolute right-0 top-10 z-50 w-80 bg-gradient-to-br from-amber-50 to-yellow-100 border-2 border-amber-400 rounded-xl shadow-2xl p-3 animate-in fade-in slide-in-from-top-2 pointer-events-auto"
              data-testid="convenience-panel-intro"
              role="dialog"
              aria-label="Customer Convenience Panel introduction"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute -top-2 right-3 w-4 h-4 bg-amber-50 border-t-2 border-l-2 border-amber-400 rotate-45" aria-hidden="true" />
              <div className="flex items-start gap-2">
                <Sparkles className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-bold text-amber-900">Customer Convenience Panel</div>
                  <div className="text-xs text-amber-800 mt-1 leading-relaxed">
                    Try it now! Create quotations for your customers using OE part numbers or JP part numbers — instantly.
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => { dismissConveniencePanelIntro(); navigate('/customer-quotations'); }}
                      className="text-xs bg-amber-600 hover:bg-amber-700 text-white font-semibold px-3 py-1 rounded transition"
                      data-testid="convenience-panel-intro-try"
                    >
                      Try Now
                    </button>
                    <button
                      onClick={dismissConveniencePanelIntro}
                      className="text-xs text-amber-700 hover:text-amber-900 px-2 py-1"
                      data-testid="convenience-panel-intro-dismiss"
                    >
                      Maybe later
                    </button>
                  </div>
                </div>
                <button
                  onClick={dismissConveniencePanelIntro}
                  className="text-amber-600 hover:text-amber-900"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Customer Cheques icon on desktop */}
      {!isAdmin && !isStaffUser && customerHasCheques && (
        <NavTooltip label="My Cheques">
        <Button
          onClick={() => navigate('/cheque-manager')}
          variant="ghost"
          className="hidden md:flex items-center text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-8 w-8 p-0"
          data-testid="desktop-my-cheques-btn"
        >
          <CreditCard className="w-4 h-4" />
        </Button>
        </NavTooltip>
      )}
      
      {/* Excel Import Button */}
      {!isViewOnly && (
        <NavTooltip label="Excel Import">
        <Button
          onClick={() => setShowExcelImport(true)}
          variant="outline"
          size="icon"
          className="hidden md:flex rounded-md h-8 w-8 border-green-300 text-green-600 hover:bg-green-50 hover:text-green-700"
          data-testid="excel-import-btn"
        >
          <FileSpreadsheet className="w-4 h-4" />
        </Button>
        </NavTooltip>
      )}
      
      {/* Admin/Staff Orders Notification Button */}
      {(isAdmin || isStaffUser) && !isViewOnly && (
        <NavTooltip label="Orders">
          <span><AdminOrdersButton /></span>
        </NavTooltip>
      )}
      
      {/* Cart */}
      {!isViewOnly && (
        <NavTooltip label="Cart">
        <Button
          onClick={() => navigate('/cart')}
          variant="outline"
          className="relative rounded-md h-8 w-8 p-0"
          data-testid="nav-cart-button"
        >
          <ShoppingCart className="w-4 h-4" />
          {totalItems > 0 && (
            <Badge
              className="absolute -top-2 -right-2 h-4 w-4 flex items-center justify-center p-0 bg-primary text-white text-[10px] rounded-full"
              data-testid="cart-badge"
            >
              {totalItems}
            </Badge>
          )}
        </Button>
        </NavTooltip>
      )}
    </>
    );
  };

  // Mobile hamburger menu items
  const MobileMenu = () => {
    // Salesman-only: just Salesman Panel + logout in mobile menu
    if (isSalesmanOnly) {
      return (
        <div className="md:hidden absolute top-16 left-0 right-0 bg-white border-b shadow-lg z-50" data-testid="mobile-menu-salesman">
          <div className="flex flex-col p-4 space-y-3">
            <Button
              onClick={() => { setMobileMenuOpen(false); navigate('/salesman'); }}
              className="w-full justify-start bg-red-600 hover:bg-red-700 text-white"
              data-testid="mobile-salesman-panel-btn"
            >
              <ShoppingCart className="w-4 h-4 mr-2" /> Salesman Panel
            </Button>
            <Button
              onClick={() => { setMobileMenuOpen(false); logout(); }}
              variant="outline"
              className="w-full justify-start"
              data-testid="mobile-logout-btn-salesman"
            >
              Logout
            </Button>
          </div>
        </div>
      );
    }
    return (
    <div className="md:hidden absolute top-16 left-0 right-0 bg-white border-b shadow-lg z-50 max-h-[70vh] overflow-y-auto" data-testid="mobile-menu">
      <div className="p-3 space-y-1">
        {!isWarehouseStaff && !vendorToken && (
          <div className="grid grid-cols-3 gap-2 pb-2 mb-1 border-b" data-testid="mobile-main-links">
            {[['Home', '/'], ['Categories', '/categories'], ['Catalog', '/catalog']].map(([label, path]) => (
              <button key={path} onClick={() => { navigate(path); setMobileMenuOpen(false); }}
                className={`py-2.5 rounded-lg text-sm font-semibold border ${location.pathname === path ? 'border-amber-500 text-amber-700 bg-amber-50' : 'border-gray-200 text-gray-700'}`}>
                {label}
              </button>
            ))}
          </div>
        )}
        {/* God Mode */}
        {isAdmin && !isViewOnly && (
          <button
            onClick={() => { navigate('/admin/god-mode'); setMobileMenuOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
            data-testid="mobile-god-mode-btn"
          >
            <Eye className="w-4 h-4" /> God View
          </button>
        )}
        
        {/* Payments */}
        {isAdmin && !isViewOnly && (
          <button
            onClick={() => { navigate('/admin/payments'); setMobileMenuOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-green-600 hover:bg-green-50 transition-colors"
            data-testid="mobile-payments-btn"
          >
            <CreditCard className="w-4 h-4" /> Payments
          </button>
        )}
        
        {/* NTO */}
        {isAdmin && !isViewOnly && DOC_SHARING_ADMINS.includes(String(user?.mobile)) && (
          <button
            onClick={() => { navigate('/nto'); setMobileMenuOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-orange-600 hover:bg-orange-50 transition-colors"
            data-testid="mobile-nto-btn"
          >
            <Boxes className="w-4 h-4" /> NTO Procurement
          </button>
        )}
        
        {/* Doc Sharing */}
        {isDocSharingAdmin && isAdmin && !isViewOnly && (
          <button
            onClick={() => { navigate('/doc-sharing'); setMobileMenuOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-indigo-600 hover:bg-indigo-50 transition-colors"
            data-testid="mobile-doc-sharing-btn"
          >
            <Mail className="w-4 h-4" /> Document Sharing
          </button>
        )}
        
        {/* Price Master */}
        {isAdmin && !isViewOnly && PRICE_MASTER_ADMINS.includes(String(user?.mobile)) && (
          <button
            onClick={() => { navigate('/price-master'); setMobileMenuOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-emerald-600 hover:bg-emerald-50 transition-colors"
            data-testid="mobile-price-master-btn"
          >
            <IndianRupee className="w-4 h-4" /> Price Master
          </button>
        )}

        {/* All Products / Dead Stock */}
        {isAdmin && !isViewOnly && (
          <button
            onClick={() => { navigate('/admin/all-products'); setMobileMenuOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-indigo-600 hover:bg-indigo-50 transition-colors"
            data-testid="mobile-all-products-btn"
          >
            <Package className="w-4 h-4" /> All Products (Dead Stock)
          </button>
        )}

        {/* Coupon & MOQ Master — Master + Bhavik + Roshan (2026-06-17) */}
        {isAdmin && !isViewOnly && COUPON_MASTER_ADMINS.includes(String(user?.mobile)) && (
          <button
            onClick={() => { navigate('/admin/coupon-master'); setMobileMenuOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-cyan-600 hover:bg-cyan-50 transition-colors"
            data-testid="mobile-coupon-master-btn"
          >
            <Tag className="w-4 h-4" /> Coupon &amp; MOQ Master
          </button>
        )}

        {/* Warehouse 2.0 */}
        {isAdmin && !isViewOnly && WAREHOUSE_2_ADMINS.includes(String(user?.mobile)) && (
          <button
            onClick={() => { navigate('/admin/warehouse-2'); setMobileMenuOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-fuchsia-600 hover:bg-fuchsia-50 transition-colors"
            data-testid="mobile-warehouse-2-btn"
          >
            <ScanLine className="w-4 h-4" /> Warehouse 2.0 (Scan Ops)
          </button>
        )}
        
        {/* Warehouse Management */}
        {isAdmin && !isViewOnly && (
          <button
            onClick={() => { navigate('/warehouse-setup'); setMobileMenuOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-amber-600 hover:bg-amber-50 transition-colors"
            data-testid="mobile-warehouse-setup-btn"
          >
            <Boxes className="w-4 h-4" /> Warehouse Mgmt
          </button>
        )}
        
        {/* Warehouse Management */}
        {isAdmin && !isViewOnly && (
          <button
            onClick={() => { navigate('/warehouse-ops'); setMobileMenuOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors"
            data-testid="mobile-warehouse-management-btn"
          >
            <PackageCheck className="w-4 h-4" /> Warehouse Management
          </button>
        )}
        
        {/* Warehouse Intelligence */}
        {isAdmin && !isViewOnly && (
          <button
            onClick={() => { navigate('/warehouse-intel'); setMobileMenuOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-indigo-600 hover:bg-indigo-50 transition-colors"
            data-testid="mobile-warehouse-intel-btn"
          >
            <BarChart3 className="w-4 h-4" /> Warehouse Intel
          </button>
        )}

        {/* Stock Trail Hub — Master + Bhavik + Roshan (sub-admin, 2026-06-17) */}
        {STOCK_TRAIL_ADMINS.includes(String(user?.mobile)) && !isViewOnly && !isCustomerView && (
          <button
            onClick={() => { navigate('/warehouse/stock-trail'); setMobileMenuOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-emerald-700 hover:bg-emerald-50 transition-colors"
            data-testid="mobile-stock-trail-btn"
          >
            <Activity className="w-4 h-4" /> Stock Trail Hub
          </button>
        )}
        
        {/* Excel Import */}
        {!isViewOnly && (
          <button
            onClick={() => { setShowExcelImport(true); setMobileMenuOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-green-600 hover:bg-green-50 transition-colors"
            data-testid="mobile-excel-import-btn"
          >
            <FileSpreadsheet className="w-4 h-4" /> Excel Import
          </button>
        )}

        <div className="border-t my-2" />
        
        {/* Profile Links */}
        <button
          onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          data-testid="mobile-profile-btn"
        >
          <User className="w-4 h-4" /> Profile
        </button>
        <button
          onClick={() => { navigate('/my-orders'); setMobileMenuOpen(false); }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          data-testid="mobile-my-orders-btn"
        >
          <Package className="w-4 h-4" /> My Orders
        </button>
        <button
          onClick={() => { navigate('/my-quotations'); setMobileMenuOpen(false); }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          data-testid="mobile-my-quotations-btn"
        >
          <FileText className="w-4 h-4" /> My Quotations
        </button>
        <button
          onClick={() => { toggleTheme(); }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          data-testid="mobile-theme-toggle-btn"
        >
          {theme === 'dark' ? (
            <><Sun className="w-4 h-4 text-amber-500" /> Switch to Light Theme</>
          ) : (
            <><Moon className="w-4 h-4 text-slate-600" /> Switch to Dark Theme</>
          )}
        </button>
        <button
          onClick={() => { dismissConveniencePanelIntro(); navigate('/customer-quotations'); setMobileMenuOpen(false); }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-amber-700 hover:bg-amber-50 transition-colors"
          data-testid="mobile-customer-quotations-btn"
        >
          <Sparkles className="w-4 h-4" /> Customer Convenience Panel
        </button>
        
        {/* Staff Panel — opens the picker dialog (test-as-self OR
            impersonate a real staff) for admins; direct-mints
            (self-only) for office staff / WH manager / WH head.
            Replaces the legacy "Staff Panel (Orders)" shortcut that
            navigated to /admin/orders — that was a different feature. */}
        {(isAdmin || canOpenStaffPanel) && !isViewOnly && (
          <>
            <div className="border-t my-2" />
            <button
              onClick={() => { setMobileMenuOpen(false); handleStaffPanelClick(); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-indigo-700 hover:bg-indigo-50 transition-colors"
              data-testid="mobile-staff-panel-btn"
            >
              <span className="text-base">👥</span> Staff Panel (warehouse worker view)
            </button>
          </>
        )}
        
        <div className="border-t my-2" />
        <button
          onClick={() => { logout(); setMobileMenuOpen(false); }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
          data-testid="mobile-logout-btn"
        >
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>
    </div>
    );
  };

  return (
    <TooltipProvider>
    <nav className={`sticky z-50 border-b border-border bg-white/95 backdrop-blur-sm shadow-sm ${isImpersonating && isImpersonating() ? 'top-9' : 'top-0'}`}>
      <div className="container mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex flex-nowrap items-center justify-between h-16">
          {/* Logo */}
          <Link to={vendorToken && !isAuthenticated ? "/vendor-dashboard" : isViewOnly ? "/my-orders" : isSalesmanOnly ? "/salesman" : "/"} className="flex items-center space-x-2 group flex-shrink-0">
            <img 
              src="/jp_logo.png" 
              alt="Jai Paras Spares" 
              className="h-9 md:h-12 w-auto"
            />
            <span className="hidden sm:block text-base md:text-xl font-heading font-bold tracking-tight uppercase whitespace-nowrap" style={{ color: '#3B4DAE' }}>
              Jai Paras Spares
            </span>
          </Link>

          {/* Navigation Links - Inline after logo with separator */}
          {isAuthenticated && !isViewOnly && !isWarehouseStaff && !vendorToken && !isSalesmanOnly && (
            <div className="hidden lg:flex items-center ml-6 space-x-4 flex-shrink-0">
              <div className="w-px h-5 bg-gray-300" />
              <Link
                to="/"
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors whitespace-nowrap"
                data-testid="nav-home-link"
              >
                Home
              </Link>
              <Link
                to="/categories"
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors whitespace-nowrap"
                data-testid="nav-categories-link"
              >
                Categories
              </Link>
              <Link
                to={`/catalog${
                  (localStorage.getItem('selectedCategory') &&
                   localStorage.getItem('selectedCategory') !== 'All Products')
                    ? `?brand=${encodeURIComponent(localStorage.getItem('selectedCategory'))}`
                    : ''
                }`}
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors whitespace-nowrap"
                data-testid="nav-catalog-link"
              >
                Catalog
              </Link>
            </div>
          )}

          {/* Right Side - Cart & Auth */}
          <div className="flex items-center gap-1 md:gap-2 ml-auto">
            {isAuthenticated && (
              <>
                {/* Desktop actions - hidden on mobile */}
                <div className="hidden md:flex items-center gap-0.5">
                  {isAdmin && !isViewOnly && !isSalesmanOnly && (
                    <AppToolsMenu
                      user={user}
                      pendingDocCount={isDocSharingAdmin ? pendingDocCount : 0}
                      onExcelImport={() => setShowExcelImport(true)}
                      onStaffPanel={() => setShowStaffPanelPicker(true)}
                    />
                  )}
                  <DesktopActions />
                </div>

                {/* Mobile: Key icons + Hamburger */}
                <div className="flex md:hidden items-center gap-1.5">
                  {/* 2026-02-28 · Admins/staff: use the same AppToolsMenu
                      that drives the desktop navbar, in compact mode.
                      Renders the FIXED shortcuts (Task manager, Admin
                      panel, Cheque manager, WhatsApp hub — each visible
                      only to permitted users) + a Tools icon that opens
                      every other admin tool. Replaces the old long row
                      of individual mobile admin icons. */}
                  {isAdmin && !isViewOnly && !isSalesmanOnly && (
                    <AppToolsMenu
                      compact
                      user={user}
                      pendingDocCount={isDocSharingAdmin ? pendingDocCount : 0}
                      onExcelImport={() => setShowExcelImport(true)}
                      onStaffPanel={() => setShowStaffPanelPicker(true)}
                    />
                  )}
                  {/* Staff Panel (SP) icon — office staff / WH manager / WH head only
                      (admins get it via the Panels dropdown, hamburger, or /staff URL).
                      Direct-mints a self-only supervisor session. */}
                  {canOpenStaffPanel && !isAdmin && !isViewOnly && (
                    <Button
                      onClick={handleStaffPanelClick}
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-indigo-700 hover:bg-indigo-50 font-bold text-[11px]"
                      data-testid="mobile-nav-staff-panel"
                      title="Staff Panel"
                    >
                      SP
                    </Button>
                  )}
                  {/* Task Manager icon — kept ONLY for warehouse-head / staff
                      users who are not admins (admins get it from AppToolsMenu
                      shortcuts above). */}
                  {!isAdmin && (isStaffUser || isWarehouseStaff) && !isViewOnly && (
                    <Button
                      onClick={() => navigate('/task-manager')}
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-blue-600 hover:bg-blue-50"
                      data-testid="mobile-nav-task-manager"
                      title="Task Manager"
                    >
                      <ClipboardList className="w-4 h-4" />
                    </Button>
                  )}
                  {/* Customer Cheque icon - visible if customer has cheques */}
                  {!isAdmin && !isStaffUser && customerHasCheques && (
                    <Button
                      onClick={() => navigate('/cheque-manager')}
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-amber-600 hover:bg-amber-50"
                      data-testid="mobile-nav-my-cheques"
                      title="My Cheques"
                    >
                      <CreditCard className="w-4 h-4" />
                    </Button>
                  )}
                  {/* Cart on mobile */}
                  {!isViewOnly && (
                    <Button
                      onClick={() => navigate('/cart')}
                      variant="outline"
                      size="icon"
                      className="relative rounded-md h-9 w-9"
                      data-testid="mobile-nav-cart-button"
                    >
                      <ShoppingCart className="w-4 h-4" />
                      {totalItems > 0 && (
                        <Badge
                          className="absolute -top-2 -right-2 h-4 w-4 flex items-center justify-center p-0 bg-primary text-white text-[10px] rounded-full"
                          data-testid="mobile-cart-badge"
                        >
                          {totalItems}
                        </Badge>
                      )}
                    </Button>
                  )}
                  {/* Hamburger */}
                  <Button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    variant="outline"
                    size="icon"
                    className="rounded-md h-9 w-9"
                    data-testid="mobile-menu-toggle"
                  >
                    {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                  </Button>
                </div>
              </>
            )}

            {/* Auth - Desktop profile dropdown */}
            <div className="hidden md:flex items-center gap-2">
              <div className="w-px h-5 bg-gray-300" />
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="rounded-md h-10 px-4"
                      data-testid="user-menu-button"
                    >
                      <User className="w-5 h-5 mr-2" />
                      {user?.name}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-white border-2 border-gray-200 shadow-xl">
                    <DropdownMenuLabel className="bg-gray-50">
                      <div>
                        <p className="font-semibold text-gray-900">{user?.name}</p>
                        <p className="text-xs text-gray-600">{user?.email}</p>
                        {isViewOnly && (
                          <p className="text-xs text-orange-600 font-semibold mt-1">
                            View Only Account
                          </p>
                        )}
                        {!isViewOnly && user?.discount_status === 'approved' && (
                          <p className="text-xs text-green-600 font-semibold mt-1">
                            Discount: {user?.discount_percentage}%
                          </p>
                        )}
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-gray-200" />
                    <DropdownMenuItem 
                      onClick={() => navigate('/profile')}
                      className="cursor-pointer hover:bg-gray-100 text-gray-900 font-medium"
                    >
                      <User className="w-4 h-4 mr-2" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/my-orders')}
                      className="cursor-pointer hover:bg-gray-100 text-gray-900 font-medium"
                      data-testid="my-orders-link"
                    >
                      <Package className="w-4 h-4 mr-2" />
                      My Orders
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate('/my-quotations')}
                      className="cursor-pointer hover:bg-gray-100 text-gray-900 font-medium"
                      data-testid="my-quotations-link"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      My Quotations
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem
                        onClick={() => navigate('/posters/new')}
                        className="cursor-pointer hover:bg-gray-100 text-gray-900 font-medium"
                        data-testid="new-item-post-link"
                      >
                        <Sparkles className="w-4 h-4 mr-2 text-red-600" />
                        Create New Item Post
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="bg-gray-200" />
                    <DropdownMenuItem
                      onClick={(e) => { e.preventDefault(); toggleTheme(); }}
                      className="cursor-pointer hover:bg-gray-100 text-gray-900 font-medium"
                      data-testid="theme-toggle-btn"
                    >
                      {theme === 'dark' ? (
                        <>
                          <Sun className="w-4 h-4 mr-2 text-amber-500" />
                          Switch to Light Theme
                        </>
                      ) : (
                        <>
                          <Moon className="w-4 h-4 mr-2 text-slate-600" />
                          Switch to Dark Theme
                        </>
                      )}
                    </DropdownMenuItem>
                    {isAdmin && !isViewOnly && (
                      <>
                        <DropdownMenuSeparator className="bg-gray-200" />
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin')}
                          className="cursor-pointer hover:bg-blue-50 text-blue-600 font-semibold"
                        >
                          <Shield className="w-4 h-4 mr-2" />
                          Admin Panel
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate('/task-manager')}
                          className="cursor-pointer hover:bg-blue-50 text-blue-600 font-medium"
                        >
                          <ClipboardList className="w-4 h-4 mr-2" />
                          Task Manager
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin/payments')}
                          className="cursor-pointer hover:bg-green-50 text-green-600 font-medium"
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Payments
                        </DropdownMenuItem>
                        {DOC_SHARING_ADMINS.includes(String(user?.mobile)) && (
                          <>
                            <DropdownMenuItem 
                              onClick={() => navigate('/nto')}
                              className="cursor-pointer hover:bg-orange-50 text-orange-600 font-medium"
                              data-testid="nto-mobile-btn"
                            >
                              <Boxes className="w-4 h-4 mr-2" />
                              NTO Procurement
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => navigate('/doc-sharing')}
                              className="cursor-pointer hover:bg-indigo-50 text-indigo-600 font-medium"
                              data-testid="doc-sharing-dropdown-btn"
                            >
                              <Mail className="w-4 h-4 mr-2" />
                              Document Sharing
                            </DropdownMenuItem>
                            {(isChequeAdmin || isMasterChequeAdmin) && (
                              <DropdownMenuItem 
                                onClick={() => navigate('/cheque-manager')}
                                className="cursor-pointer hover:bg-amber-50 text-amber-600 font-medium"
                                data-testid="cheque-manager-dropdown-btn"
                              >
                                <CreditCard className="w-4 h-4 mr-2" />
                                Cheque Manager
                              </DropdownMenuItem>
                            )}
                          </>
                        )}
                        <DropdownMenuItem 
                          onClick={() => navigate('/warehouse-setup')}
                          className="cursor-pointer hover:bg-amber-50 text-amber-600 font-medium"
                          data-testid="warehouse-setup-dropdown-btn"
                        >
                          <Boxes className="w-4 h-4 mr-2" />
                          Warehouse Mgmt
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate('/warehouse-ops')}
                          className="cursor-pointer hover:bg-rose-50 text-rose-600 font-medium"
                          data-testid="warehouse-management-dropdown-btn"
                        >
                          <PackageCheck className="w-4 h-4 mr-2" />
                          Warehouse Management
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate('/warehouse-intel')}
                          className="cursor-pointer hover:bg-indigo-50 text-indigo-600 font-medium"
                          data-testid="warehouse-intel-dropdown-btn"
                        >
                          <BarChart3 className="w-4 h-4 mr-2" />
                          Warehouse Intel
                        </DropdownMenuItem>
                        {ANALYTICS_ADMINS.includes(String(user?.mobile)) && (
                          <DropdownMenuItem
                            onClick={() => navigate('/analytics')}
                            className="cursor-pointer hover:bg-cyan-50 text-cyan-600 font-medium"
                            data-testid="analytics-dropdown-btn"
                          >
                            <BarChart2 className="w-4 h-4 mr-2" />
                            Analytics
                          </DropdownMenuItem>
                        )}
                        {STOCK_TRAIL_ADMINS.includes(String(user?.mobile)) && (
                          <DropdownMenuItem
                            onClick={() => navigate('/warehouse/stock-trail')}
                            className="cursor-pointer hover:bg-emerald-50 text-emerald-700 font-medium"
                            data-testid="stock-trail-dropdown-btn"
                          >
                            <BarChart3 className="w-4 h-4 mr-2" />
                            Stock Trail Hub
                          </DropdownMenuItem>
                        )}
                      </>
                    )}
                    {!isAdmin && isStaffUser && !isViewOnly && (
                      <>
                        <DropdownMenuSeparator className="bg-gray-200" />
                        <DropdownMenuItem 
                          onClick={() => navigate('/admin/orders')}
                          className="cursor-pointer hover:bg-orange-50 text-orange-600 font-semibold"
                        >
                          <Package className="w-4 h-4 mr-2" />
                          Staff Panel (Orders)
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate('/task-manager')}
                          className="cursor-pointer hover:bg-blue-50 text-blue-600 font-medium"
                        >
                          <ClipboardList className="w-4 h-4 mr-2" />
                          Task Manager
                        </DropdownMenuItem>
                      </>
                    )}
                    {/* 2026-07-21 · RC-146 — warehouse head / manager
                        dropdown Task Manager shortcut. */}
                    {!isAdmin && !isStaffUser && isWarehouseStaff && !isViewOnly && (
                      <>
                        <DropdownMenuSeparator className="bg-gray-200" />
                        <DropdownMenuItem
                          onClick={() => navigate('/task-manager')}
                          className="cursor-pointer hover:bg-blue-50 text-blue-600 font-medium"
                          data-testid="wh-dropdown-task-manager"
                        >
                          <ClipboardList className="w-4 h-4 mr-2" />
                          Task Manager
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator className="bg-gray-200" />
                    <DropdownMenuItem 
                      onClick={logout} 
                      className="cursor-pointer hover:bg-red-50 text-red-600 font-semibold"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : vendorToken ? (
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => navigate('/vendor-dashboard')}
                    variant="outline"
                    className="rounded-md h-10 px-4 border-orange-300 text-orange-700 hover:bg-orange-50"
                    data-testid="vendor-dashboard-btn"
                  >
                    <Truck className="w-4 h-4 mr-2" />
                    <span className="text-sm">{vendorInfo?.company_name || 'Vendor Panel'}</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 h-10 px-3"
                    onClick={() => {
                      localStorage.removeItem('vendor_token');
                      setVendorToken(null);
                      setVendorInfo(null);
                      navigate('/auth');
                    }}
                    data-testid="vendor-logout-navbar-btn"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => navigate('/auth')}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-md h-10 px-6"
                  data-testid="login-button"
                >
                  Login / Register
                </Button>
              )}
            </div>

            {/* Mobile: Auth for non-authenticated / vendor users */}
            <div className="md:hidden">
              {!isAuthenticated && !vendorToken && (
                <Button
                  onClick={() => navigate('/auth')}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-md h-9 px-4 text-sm"
                  data-testid="mobile-login-button"
                >
                  Login
                </Button>
              )}
              {vendorToken && !isAuthenticated && (
                <div className="flex items-center gap-1">
                  <Button
                    onClick={() => navigate('/vendor-dashboard')}
                    variant="outline"
                    size="sm"
                    className="border-orange-300 text-orange-700 hover:bg-orange-50 h-9 px-3 text-xs"
                    data-testid="mobile-vendor-btn"
                  >
                    <Truck className="w-3.5 h-3.5 mr-1" />
                    Vendor
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 h-9 w-9 p-0"
                    onClick={() => {
                      localStorage.removeItem('vendor_token');
                      setVendorToken(null);
                      setVendorInfo(null);
                      navigate('/auth');
                    }}
                    data-testid="mobile-vendor-logout-btn"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && isAuthenticated && <MobileMenu />}
      
      {/* Excel Import Modal */}
      <ExcelImportQuotation
        isOpen={showExcelImport}
        onClose={() => setShowExcelImport(false)}
        isAdmin={isAdmin || isStaffUser}
        onQuotationCreated={() => {
          setShowExcelImport(false);
        }}
      />

      {/* Staff Panel picker — admin chooses to test as self OR impersonate a staff member */}
      <StaffPanelPickerDialog
        open={showStaffPanelPicker}
        onClose={() => setShowStaffPanelPicker(false)}
      />
    </nav>
    </TooltipProvider>
  );
};

export default Navbar;
